import {
  FlexibilityViewModel,
  ShiftScheduleViewModel,
  ShiftRowViewModel,
  ShiftLayoutVariant,
  ShiftSlotViewModel,
  ComparisonDayViewModel,
  ComparisonViewModel,
  CandidateSlotViewModel,
} from './RequisitionDetailsViewModel';
import { weekDays, weekEnds } from '../../../../../utils/uiUtils';
import { NewRequisition, NewShift, Requisition, ShiftRequirement, ShiftDetail, ShiftDays } from '../types/requistionDetails';
import { Shifts } from '../../../../../firebase/firestore/documents/requisition';
import { formatShiftTime, isOvernightShift, formatMinHours, getDayRangeLabel } from './shiftTimeUtils';
import { CandidateSlotRaw, collectCandidateSlots, checkSlotCoverage, applyMismatchFlag } from './availabilityCoverage';

// Re-export time utilities so existing consumers don't break
export { formatShiftTime, isOvernightShift, formatMinHours, getDayRangeLabel } from './shiftTimeUtils';

/** Internal-only extension of CandidateSlotViewModel that carries raw HH:mm strings for coverage checks */
interface CandidateSlotInternal extends CandidateSlotViewModel {
  rawStart?: string;
  rawEnd?: string;
}

const EMPTY_SLOT: ShiftSlotViewModel = {
  timeLabel: undefined,
  isRequired: false,
  exists: false,
};

const buildSlot = (time?: string, required?: boolean): ShiftSlotViewModel =>
  time ? { timeLabel: formatShiftTime(time), isRequired: !!required, exists: true } : EMPTY_SLOT;

// Layout Detection
/**
 * 'double' when ANY requirement carries a second time slot.
 * This drives the column count for the entire table.
 */
const detectVariant = (reqs: ShiftRequirement[]): ShiftLayoutVariant => {
  if (!reqs) return 'single';
  return reqs.some((r) => r.schedule.start_2 || r.schedule.end_2) ? 'double' : 'single';
};

// Flexibility View-Model

export const buildFlexibilityViewModel = (shifts: NewShift, comparison?: ComparisonViewModel): FlexibilityViewModel => {
  let numOfStartsWeekdayMismatch;
  let numOfEndsWeekdayMismatch;
  let numOfStartsWeekendMismatch;
  let numOfEndsWeekendMismatch;
  let mandatoryDaysWeekdayMismatch;
  let mandatoryDaysWeekendMismatch;

  // Compute required start/end counts and mandatory days from requirements.
  const weekDaySet = new Set(weekDays);
  const computedCounts = { startsWeekday: 0, endsWeekday: 0, startsWeekend: 0, endsWeekend: 0 };
  const mandatoryDaysWeekday: string[] = [];
  const mandatoryDaysWeekend: string[] = [];
  (shifts.requirements || []).forEach((req) => {
    const isWeekday = weekDaySet.has(req.day);
    // Collect days with the day-required flag
    if (req.required) {
      if (isWeekday) mandatoryDaysWeekday.push(req.day);
      else mandatoryDaysWeekend.push(req.day);
    }
    if (req.schedule.start_1_required) {
      if (isWeekday) computedCounts.startsWeekday += 1;
      else computedCounts.startsWeekend += 1;
    }
    if (req.schedule.start_2_required) {
      if (isWeekday) computedCounts.startsWeekday += 1;
      else computedCounts.startsWeekend += 1;
    }
    if (req.schedule.end_1_required) {
      if (isWeekday) computedCounts.endsWeekday += 1;
      else computedCounts.endsWeekend += 1;
    }
    if (req.schedule.end_2_required) {
      if (isWeekday) computedCounts.endsWeekday += 1;
      else computedCounts.endsWeekend += 1;
    }
  });

  const numOfStartsWeekday = shifts.numOfStartsWeekday ?? computedCounts.startsWeekday;
  const numOfEndsWeekday = shifts.numOfEndsWeekday ?? computedCounts.endsWeekday;
  const numOfStartsWeekend = shifts.numOfStartsWeekend ?? computedCounts.startsWeekend;
  const numOfEndsWeekend = shifts.numOfEndsWeekend ?? computedCounts.endsWeekend;

  if (comparison) {
    const { weekdayRows, weekendRows } = comparison;

    // Count each individual required start/end slot that the candidate covers.
    // A required slot counts as "covered" when it is marked isRequired and NOT isMismatch.
    const countCoverage = (rows: ComparisonDayViewModel[]) => {
      let startsCovered = 0;
      let endsCovered = 0;
      rows.forEach((r) => {
        if (!r.isRowMismatch) {
          if (r.desiredSlot1Start.isRequired && !r.desiredSlot1Start.isMismatch) startsCovered += 1;
          if (r.desiredSlot1End.isRequired && !r.desiredSlot1End.isMismatch) endsCovered += 1;
          if (r.desiredSlot2Start?.isRequired && !r.desiredSlot2Start.isMismatch) startsCovered += 1;
          if (r.desiredSlot2End?.isRequired && !r.desiredSlot2End.isMismatch) endsCovered += 1;
        }
      });
      return { startsCovered, endsCovered };
    };
    const weekday = countCoverage(weekdayRows);
    const weekend = countCoverage(weekendRows);

    numOfStartsWeekdayMismatch = numOfStartsWeekday > weekday.startsCovered;
    numOfEndsWeekdayMismatch = numOfEndsWeekday > weekday.endsCovered;
    numOfStartsWeekendMismatch = numOfStartsWeekend > weekend.startsCovered;
    numOfEndsWeekendMismatch = numOfEndsWeekend > weekend.endsCovered;

    // A mandatory-day mismatch occurs when any day with required=true has isDayMismatch in the comparison.
    const hasDayMismatch = (rows: ComparisonDayViewModel[], mandatoryKeys: string[]) => {
      if (mandatoryKeys.length === 0) return false;
      const keySet = new Set(mandatoryKeys);
      return rows.some((r) => keySet.has(r.id) && r.isDayMismatch);
    };
    mandatoryDaysWeekdayMismatch = hasDayMismatch(weekdayRows, mandatoryDaysWeekday);
    mandatoryDaysWeekendMismatch = hasDayMismatch(weekendRows, mandatoryDaysWeekend);
  }

  return {
    weekdayCount: weekDays.length, // 5
    weekendCount: weekEnds.length, // 2
    mandatoryDaysWeekday,
    mandatoryDaysWeekend,
    mandatoryDaysWeekdayMismatch,
    mandatoryDaysWeekendMismatch,
    numOfStartsWeekday,
    numOfEndsWeekday,
    numOfStartsWeekend,
    numOfEndsWeekend,
    numOfStartsWeekdayMismatch,
    numOfEndsWeekdayMismatch,
    numOfStartsWeekendMismatch,
    numOfEndsWeekendMismatch,
  };
};

// Shift Row Builder

const buildRow = (
  req: ShiftRequirement,
  variant: ShiftLayoutVariant,
  t: (key: string) => string,
  candAvail?: Record<string, ShiftDetail>,
  candNightAvail?: Record<string, ShiftDetail>,
): ShiftRowViewModel => {
  const { day, required, schedule, minHours } = req;
  const overnight = isOvernightShift(schedule.start_1, schedule.end_1) || isOvernightShift(schedule.start_2, schedule.end_2);

  const slot1Start = buildSlot(schedule.start_1, schedule.start_1_required);
  const slot1End = buildSlot(schedule.end_1, schedule.end_1_required);
  let slot2Start;
  let slot2End;

  if (variant === 'double') {
    slot2Start = buildSlot(schedule.start_2, schedule.start_2_required);
    slot2End = buildSlot(schedule.end_2, schedule.end_2_required);
  }

  // Check candidate overlap if availability data is provided
  let isRowMismatch = false;
  if (candAvail) {
    const candidateSlots = collectCandidateSlots(candAvail[day], candNightAvail?.[day]);

    const s1Coverage = checkSlotCoverage(schedule.start_1, schedule.end_1, candidateSlots);
    const s2Coverage = checkSlotCoverage(schedule.start_2, schedule.end_2, candidateSlots);

    applyMismatchFlag(slot1Start, schedule.start_1_required, s1Coverage.startCovered);
    applyMismatchFlag(slot1End, schedule.end_1_required, s1Coverage.endCovered);

    if (slot2Start && slot2End) {
      applyMismatchFlag(slot2Start, schedule.start_2_required, s2Coverage.startCovered);
      applyMismatchFlag(slot2End, schedule.end_2_required, s2Coverage.endCovered);
    }

    const isDayMismatch =
      !!required && (!s1Coverage.startCovered || !s1Coverage.endCovered || !s2Coverage.startCovered || !s2Coverage.endCovered);
    isRowMismatch =
      isDayMismatch || !!slot1Start.isMismatch || !!slot1End.isMismatch || !!slot2Start?.isMismatch || !!slot2End?.isMismatch;
  }

  return {
    id: day,
    dayLabel: overnight ? getDayRangeLabel(day, t) : t(`common:days.${day}`),
    isDayRequired: !!required,
    isOvernight: overnight,
    slot1Start,
    slot1End,
    slot2Start,
    slot2End,
    minHoursLabel: formatMinHours(minHours),
    isRowMismatch,
  };
};

//  Row Group Builder

/**
 * Builds an ordered row list for the given day group (weekdays or weekends).
 * Days absent from the requirements array are rendered as empty rows
 * so the table always shows all 5 weekdays and 2 weekend days.
 */
const buildRowsForDays = (
  days: string[],
  reqMap: Map<string, ShiftRequirement>,
  variant: ShiftLayoutVariant,
  t: (key: string) => string,
  candAvail?: Record<string, ShiftDetail>,
  candNightAvail?: Record<string, ShiftDetail>,
): ShiftRowViewModel[] =>
  days.map((day) => {
    const req = reqMap.get(day);
    if (req) return buildRow(req, variant, t, candAvail, candNightAvail);

    // Placeholder row for days with no requirement
    const empty: ShiftRowViewModel = {
      id: day,
      dayLabel: t(`common:days.${day}`),
      isDayRequired: false,
      isOvernight: false,
      slot1Start: EMPTY_SLOT,
      slot1End: EMPTY_SLOT,
      minHoursLabel: undefined,
    };
    if (variant === 'double') {
      empty.slot2Start = EMPTY_SLOT;
      empty.slot2End = EMPTY_SLOT;
    }
    return empty;
  });

// Shift Schedule View-Model

export const buildShiftScheduleViewModel = (
  shifts: NewShift,
  t: (key: string) => string,
  candidateAvailabilities?: Record<string, ShiftDetail>,
  candidateNightAvailabilities?: Record<string, ShiftDetail>,
): ShiftScheduleViewModel => {
  const { requirements = [] } = shifts;
  const variant = detectVariant(requirements);
  const reqMap = new Map(requirements.map((r) => [r.day, r]));

  return {
    variant,
    weekdayRows: buildRowsForDays(weekDays, reqMap, variant, t, candidateAvailabilities, candidateNightAvailabilities),
    weekendRows: buildRowsForDays(weekEnds, reqMap, variant, t, candidateAvailabilities, candidateNightAvailabilities),
    hasComparisonData: !!(candidateAvailabilities || candidateNightAvailabilities),
  };
};

// Overlap Schedule Builder

/**
 * Shape of a single day's entry in the raw intersection-shift map passed
 * into `buildOverlapShiftSchedule`. Slots 1/2 are "day" slots, slots 3/4
 * are "night" slots, used as a fallback when no day slot is present.
 */
export interface IntersectionDayDetail {
  start_1?: string;
  end_1?: string;
  start_2?: string;
  end_2?: string;
  start_3?: string;
  end_3?: string;
  start_4?: string;
  end_4?: string;
}

export const buildOverlapShiftSchedule = (
  requisition: NewRequisition,
  intersectionShift: Record<string, IntersectionDayDetail>,
): NewShift => {
  const requirements: ShiftRequirement[] = [];
  const days: ShiftDays[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  days.forEach((day) => {
    const detail = intersectionShift[day];
    // Include day if it has any slot — day (start_1, start_2) or night (start_3, start_4)
    if (
      detail &&
      (detail.start_1 ||
        detail.end_1 ||
        detail.start_2 ||
        detail.end_2 ||
        detail.start_3 ||
        detail.end_3 ||
        detail.start_4 ||
        detail.end_4)
    ) {
      const desiredReq = requisition.shifts?.requirements?.find((r) => r.day === day);

      // Day/Night overlap mapping:
      // Slot 1 gets day slot 1 (start_1) or night slot 1 (start_3) as fallback.
      // Slot 2 gets day slot 2 (start_2) or night slot 2 (start_4) as fallback.
      const resolvedStart1 = detail.start_1 || detail.start_3;
      const resolvedEnd1 = detail.end_1 || detail.end_3;
      const resolvedStart2 = detail.start_2 || detail.start_4;
      const resolvedEnd2 = detail.end_2 || detail.end_4;

      requirements.push({
        day,
        required: desiredReq?.required,
        minHours: desiredReq?.minHours,
        schedule: {
          start_1: resolvedStart1,
          start_1_required: desiredReq?.schedule?.start_1_required || false,
          end_1: resolvedEnd1,
          end_1_required: desiredReq?.schedule?.end_1_required || false,
          start_2: resolvedStart2,
          start_2_required: desiredReq?.schedule?.start_2_required || false,
          end_2: resolvedEnd2,
          end_2_required: desiredReq?.schedule?.end_2_required || false,
        },
      });
    }
  });

  return {
    requirements,
    numOfStartsWeekday: requisition.shifts?.numOfStartsWeekday,
    numOfEndsWeekday: requisition.shifts?.numOfEndsWeekday,
    numOfStartsWeekend: requisition.shifts?.numOfStartsWeekend,
    numOfEndsWeekend: requisition.shifts?.numOfEndsWeekend,
  };
};

//   Comparison View-Model

export const buildComparisonViewModel = (
  shifts: NewShift,
  candidateAvailabilities: Record<string, ShiftDetail> = {},
  candidateNightAvailabilities: Record<string, ShiftDetail> = {},
  t: (key: string) => string,
): ComparisonViewModel => {
  const { requirements = [] } = shifts;
  const variant = detectVariant(requirements);
  const reqMap = new Map(requirements.map((r) => [r.day, r]));

  const buildDayComparison = (day: ShiftDays): ComparisonDayViewModel => {
    const req = reqMap.get(day);
    const overnight =
      req &&
      (isOvernightShift(req.schedule.start_1, req.schedule.end_1) || isOvernightShift(req.schedule.start_2, req.schedule.end_2));

    const dayLabel = overnight ? getDayRangeLabel(day, t) : t(`common:days.${day}`);

    const isDayRequired = !!req?.required;

    const dayAvail = candidateAvailabilities[day];
    const nightAvail = candidateNightAvailabilities[day];

    // Flatten candidate slots for this day, preserving all slots
    const candidateSlots: CandidateSlotInternal[] = [];
    const pushCandidateSlot = (start: string, end: string) => {
      candidateSlots.push({
        startLabel: formatShiftTime(start),
        endLabel: formatShiftTime(end),
        rawStart: start,
        rawEnd: end,
      });
    };

    if (dayAvail) {
      if (dayAvail.start_1 && dayAvail.end_1) pushCandidateSlot(dayAvail.start_1, dayAvail.end_1);
      if (dayAvail.start_2 && dayAvail.end_2) pushCandidateSlot(dayAvail.start_2, dayAvail.end_2);
    }
    if (nightAvail) {
      if (nightAvail.start_1 && nightAvail.end_1) pushCandidateSlot(nightAvail.start_1, nightAvail.end_1);
      if (nightAvail.start_2 && nightAvail.end_2) pushCandidateSlot(nightAvail.start_2, nightAvail.end_2);
    }

    if (candidateSlots.length === 0) {
      // Ensure at least one empty slot so UI renders a row
      candidateSlots.push({ startLabel: undefined, endLabel: undefined });
    }

    if (!req) {
      return {
        id: day,
        dayLabel,
        isDayRequired: false,
        desiredSlot1Start: EMPTY_SLOT,
        desiredSlot1End: EMPTY_SLOT,
        candidateSlots,
      };
    }

    // Use coverageChecker for unified coverage logic
    const rawSlots: CandidateSlotRaw[] = candidateSlots
      .filter((c): c is CandidateSlotInternal & { rawStart: string; rawEnd: string } => !!c.rawStart && !!c.rawEnd)
      .map((c) => ({ rawStart: c.rawStart, rawEnd: c.rawEnd }));

    const s1Coverage = checkSlotCoverage(req.schedule.start_1, req.schedule.end_1, rawSlots);
    const s2Coverage = checkSlotCoverage(req.schedule.start_2, req.schedule.end_2, rawSlots);

    const desiredSlot1Start = buildSlot(req.schedule.start_1, req.schedule.start_1_required);
    const desiredSlot1End = buildSlot(req.schedule.end_1, req.schedule.end_1_required);
    applyMismatchFlag(desiredSlot1Start, req.schedule.start_1_required, s1Coverage.startCovered);
    applyMismatchFlag(desiredSlot1End, req.schedule.end_1_required, s1Coverage.endCovered);

    const desiredSlot2Start = variant === 'double' ? buildSlot(req.schedule.start_2, req.schedule.start_2_required) : undefined;
    const desiredSlot2End = variant === 'double' ? buildSlot(req.schedule.end_2, req.schedule.end_2_required) : undefined;
    if (desiredSlot2Start) applyMismatchFlag(desiredSlot2Start, req.schedule.start_2_required, s2Coverage.startCovered);
    if (desiredSlot2End) applyMismatchFlag(desiredSlot2End, req.schedule.end_2_required, s2Coverage.endCovered);

    const minHoursMismatch = false;

    const isDayMismatch =
      isDayRequired && (!s1Coverage.startCovered || !s1Coverage.endCovered || !s2Coverage.startCovered || !s2Coverage.endCovered);
    const isRowMismatch =
      isDayMismatch ||
      desiredSlot1Start.isMismatch ||
      desiredSlot1End.isMismatch ||
      desiredSlot2Start?.isMismatch ||
      desiredSlot2End?.isMismatch ||
      minHoursMismatch;

    return {
      id: day,
      dayLabel,
      isDayRequired,
      isDayMismatch,
      isRowMismatch,
      desiredSlot1Start,
      desiredSlot1End,
      desiredSlot2Start,
      desiredSlot2End,
      minHoursLabel: formatMinHours(req.minHours),
      minHoursMismatch,
      candidateSlots,
    };
  };

  const weekdayRows = (weekDays as ShiftDays[]).map(buildDayComparison);
  const weekendRows = (weekEnds as ShiftDays[]).map(buildDayComparison);

  const meetsCriteria = !weekdayRows.some((r) => r.isRowMismatch) && !weekendRows.some((r) => r.isRowMismatch);

  const requiredDays = [...weekdayRows, ...weekendRows].filter((r) => r.isDayRequired);
  const hasAnyMatch = requiredDays.every((r) => !r.isRowMismatch);

  return {
    variant,
    meetsCriteria,
    hasAnyMatch,
    weekdayRows,
    weekendRows,
  };
};

//  Type Guards

export function isNewShift(shifts: Shifts | NewShift): shifts is NewShift {
  return shifts != null && 'requirements' in shifts && Array.isArray((shifts as NewShift).requirements);
}

export function isNewRequisition(requisition: Requisition | NewRequisition | undefined): requisition is NewRequisition {
  return requisition != null && requisition.shifts != null && isNewShift(requisition.shifts);
}
